import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Download } from '@mui/icons-material';

import {
  buildCsv,
  downloadBlobFile,
  downloadTextFile,
  getDefaultReportPeriod,
  getReportErrorMessage,
  getReportPeriodDays,
  ReportEmptyState,
  ReportErrorState,
  ReportPeriod,
  ReportPeriodFilter,
  ReportsNavigation,
} from '../../components/reports/ReportFilters';
import reportsService, { ContentStatisticsResponse } from '../../services/reportsService';

const ContentStatisticsPage: React.FC = () => {
  const [stats, setStats] = useState<ContentStatisticsResponse | null>(null);
  const [period, setPeriod] = useState<ReportPeriod>(() => getDefaultReportPeriod());
  const [appliedPeriod, setAppliedPeriod] = useState<ReportPeriod>(() => getDefaultReportPeriod());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setStats(await reportsService.getContentStatistics({ days: getReportPeriodDays(appliedPeriod) }));
      setError(null);
    } catch (err) {
      setError(getReportErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [appliedPeriod]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = () => {
    if (!stats) return;

    const csv = buildCsv(stats.content_playback, [
      { header: 'content_id', value: (item) => item.content_id },
      { header: 'content_name', value: (item) => item.content_name },
      { header: 'content_type', value: (item) => item.content_type },
      { header: 'total_plays', value: (item) => item.total_plays },
      { header: 'unique_devices', value: (item) => item.unique_devices },
      { header: 'completion_rate', value: (item) => item.completion_rate },
    ]);
    downloadTextFile(csv, `content-statistics-${appliedPeriod.dateFrom}-${appliedPeriod.dateTo}.csv`);
  };

  const exportExcel = async () => {
    try {
      setExporting(true);
      const file = await reportsService.exportReport(
        'content-statistics',
        { days: getReportPeriodDays(appliedPeriod) },
        'xlsx',
      );
      downloadBlobFile(file, `content-statistics-${appliedPeriod.dateFrom}-${appliedPeriod.dateTo}.xlsx`);
      setError(null);
    } catch (err) {
      setError(getReportErrorMessage(err, 'Не удалось экспортировать статистику контента в Excel'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Статистика контента</Typography>
          <Typography variant="body2" color="text.secondary">
            Распределение медиа, хранилище и воспроизведения за выбранный период.
          </Typography>
        </Box>

        <ReportsNavigation />

        <ReportPeriodFilter
          period={period}
          onPeriodChange={setPeriod}
          onApply={() => setAppliedPeriod(period)}
          loading={loading}
          extraActions={(
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={exportExcel}
                disabled={loading || exporting || !stats}
              >
                Excel
              </Button>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={exportCsv}
                disabled={loading || !stats || stats.content_playback.length === 0}
              >
                CSV
              </Button>
            </Stack>
          )}
        />

        {error && <ReportErrorState title="Статистика контента недоступна" message={error} onRetry={load} />}

        {loading || !stats ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}><Paper sx={{ p: 2 }}><Typography color="text.secondary">Медиа</Typography><Typography variant="h5">{stats.total_media}</Typography></Paper></Grid>
              <Grid item xs={12} md={3}><Paper sx={{ p: 2 }}><Typography color="text.secondary">Плейлисты</Typography><Typography variant="h5">{stats.total_playlists}</Typography></Paper></Grid>
              <Grid item xs={12} md={3}><Paper sx={{ p: 2 }}><Typography color="text.secondary">Хранилище</Typography><Typography variant="h5">{stats.storage_used_mb} MB</Typography></Paper></Grid>
              <Grid item xs={12} md={3}><Paper sx={{ p: 2 }}><Typography color="text.secondary">Воспроизведения</Typography><Typography variant="h5">{stats.playback_count_30d}</Typography></Paper></Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>Типы медиа</Typography>
                  {Object.entries(stats.by_type).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Медиафайлы не найдены.</Typography>
                  ) : (
                    Object.entries(stats.by_type).map(([type, count]) => (
                      <Typography key={type}>{type}: {count}</Typography>
                    ))
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={8}>
                <Paper>
                  {stats.content_playback.length === 0 ? (
                    <ReportEmptyState title="Нет воспроизведений контента" message="За выбранный период нет данных для таблицы." />
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Контент</TableCell>
                          <TableCell>Тип</TableCell>
                          <TableCell>Показы</TableCell>
                          <TableCell>Устройства</TableCell>
                          <TableCell>Завершенность</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stats.content_playback.slice(0, 15).map((item) => (
                          <TableRow key={`${item.content_type}-${item.content_id}`}>
                            <TableCell>{item.content_name}</TableCell>
                            <TableCell>{item.content_type}</TableCell>
                            <TableCell>{item.total_plays}</TableCell>
                            <TableCell>{item.unique_devices}</TableCell>
                            <TableCell>{item.completion_rate.toFixed(1)}%</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
      </Stack>
    </Box>
  );
};

export default ContentStatisticsPage;
